import { mapState } from "vuex"

import SosSelect from "@/components/SosSelect"
import ConfirmDialog from "@/modals/ConfirmDialog"
import SearchUserAndInvite from "@/modals/SearchUserAndInvite"
import AddUserToScaleUserListItem from "@/components/AddUserToScaleUserListItem"

import { CHECK_RULE_VIOLATION } from "@/store/working-hours-rules/types"
import { fbFullUpdate, apiIsUserInDuty, createShift, apiCheckUserDutyConflictInGroup } from "@/services/firebase"
import { getUserUID } from "@/services/endpoints/session"
import { useRecentUsersStorage } from "@/composables/useRecentUsersStorage"
import { formatDbTime } from "@/mixins/utils"
import confirmDialogMixin from "@/mixins/cd.mixin.js"
import { parseYYYYMD, formatDDMMYYYY } from "@/functions/datetime"
import { isUserPlanOld } from "@/utils.js"
import { canAddUserShift } from "@/functions"
import { freeVersion } from "@/constants"
import { logDebug } from '@/functions/debug'
import { EditScaleUsersCache } from '@/composables/useStorage'

// Just for testing for now
// const freeVersion = false

//
//
//

// remove tudo que não for letra ou número
const normalizeSearchText = (value = '') => {
  return String(value)
    .toLowerCase()
    .normalize('NFD') // remove acentos
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
}

const userMatchesSearch = (user, rawSearch) => {
  if (!rawSearch) return true

  const search = normalizeSearchText(rawSearch)
  if (!search) return true

  return user._searchIndex.includes(search)
}

const buildSearchIndex = (user) => {
  const fields = [user.name, user.email, user.cpf, user.cnpj, user.phone]
    .filter(Boolean)
    .join(' ')

  return normalizeSearchText(fields)
}

//
//
//

const SEARCH_MIN_LENGTH = 3

const DEFAULT_ADD_ARGS = () => ({
  checkScaleDutyManualConflicts: true,
  checkScaleDutyJourneyConflicts: true,
  checkDutyConflicts: true,
  checkRuleViolations: true,
})

//
//
//

export default {
  name: 'AddUserToScaleModal',

  components: {
    'sos-select': SosSelect,
    'confirm-dialog': ConfirmDialog,
    'add-user-to-scale-user-list-item': AddUserToScaleUserListItem,
    'search-user-and-invite': SearchUserAndInvite,
  },

  mixins: [
    confirmDialogMixin,
  ],

  data ()
  {
    return {
      visible: false,
      _closingIntentionally: false,

      freeVersion,

      SHOW_HINT: false,

      shiftDutyTaskStartDateDDMMYYYY: null,
      shiftDutyTaskStartDateYYYYMD: null,

      loading: false,
      isMultiSelectActive: false,
      selectedUsers: [],
      usuariosRecentes: [],

      scaleID: '',
      scaleName: '',
      scaleLocation: { id: '', name: '' },
      scaleTurns: [],
      scaleUsers: null,
      /*
      // [staging-portaltelemedicina / master-portaltelemedicina]
      scaleConsultationDuration: 0,
      slotType: "FREE",
      // [staging-portaltelemedicina / master-portaltelemedicina]
      */
      // Valores de precificação da escala
      scaleValue: 0,
      scaleWeekdaysHourlyRate: 0,
      scaleWeekendHourlyRate: 0,
      scaleDayShiftHourlyRate: 0,
      scaleNightShiftHourlyRate: 0,
      scaleWeekdaysDayShiftHourlyRate: 0,
      scaleWeekdaysNightShiftHourlyRate: 0,
      scaleWeekendDayShiftHourlyRate: 0,
      scaleWeekendNightShiftHourlyRate: 0,

      groupID: '',
      grupoNome: '',

      turnID: '',
      turnoDados: null,

      myUser: null,
      lastUsersUpdateAt: null,
      rawUsers: [],

      // Paginação
      page: 1,
      perPage: 50,

      // Seleção múltipla
      _multiSelectIndex: 0,
      _currentContextUser: null,
      _currentContextArgs: null,

      // Termos de busca
      search: '',
      debouncedSearch: "",
    }
  },

  computed: {
    ...mapState({
      userSession: state => state.app.userSession,
    }),

    //
    //
    //

    TOTAL_VISIBLE() {
      return this.visibleUsers.length
    },

    //
    //
    //

    TOTAL_GLOBAL() {
      return this.rawUsers.length
    },

    //
    //
    //

    PAGE_START_INDEX() {
      if (!this.TOTAL_VISIBLE) return 0
      return ((this.page - 1) * this.perPage) + 1
    },

    //
    //
    //

    PAGE_END_INDEX() {
      return Math.min(this.page * this.perPage, this.TOTAL_VISIBLE)
    },

    //
    //
    //

    footerCountText() {
      // Busca
      if (this.isModeSearch) return `Mostrando ${this.PAGE_START_INDEX}-${this.PAGE_END_INDEX} de ${this.TOTAL_VISIBLE} (resultado da busca)`
      // Plano limitando
      if (this.USER_LIST_IS_LIMITED) return `Mostrando ${this.PAGE_START_INDEX}-${this.PAGE_END_INDEX} de ${this.TOTAL_VISIBLE}` // (Disponíveis no plano: ${this.planUsers.allowed.length})
      // Normal
      return `Mostrando ${this.PAGE_START_INDEX}-${this.PAGE_END_INDEX} de ${this.TOTAL_VISIBLE}`
    },

    //
    //
    //

    pageRanges() {
      // if (this.isModeSearch) return []
      if (!this.visibleUsers.length) return []

      const ranges = []
      const totalPages = this.TOTAL_PAGES

      for (let p = 1; p <= totalPages; p++) {
        const start = (p - 1) * this.perPage
        const end = Math.min(start + this.perPage, this.visibleUsers.length)

        const slice = this.visibleUsers.slice(start, end)

        // remove itens artificiais
        const pureUsers = slice.filter(u => {
          return u.name && (
            !u._label ||
            u._label === 'BLOCKED' ||
            (this.isModeSearch && ["ME", "RECENT"].includes(u._label))
          )
        })

        let first = pureUsers[0]?.name?.[0] || ""
        first = String(first).toLocaleUpperCase()
        let last = pureUsers.at(-1)?.name?.[0] || ""
        last = String(last).toLocaleUpperCase()

        // const first = slice[0]?.name?.[0]
        // const last = slice.at(-1)?.name?.[0]

        // ranges.push({ page: p, label: p, letterRange: first && last ? `${first}–${last}` : "" })

        ranges.push({
          key: `page_${p}`,
          value: String(p),
          text: `Página ${p} de ${this.TOTAL_PAGES}${first && last ? ` (${first}–${last})` : ""}`,
        })
      }

      return ranges
    },

    //
    //
    //

    TOTAL_PAGES() {
      return Math.max(1, Math.ceil(this.visibleUsers.length / this.perPage))
    },

    //
    //
    //

    PAGINATED_VISIBLE_USERS() {
      const start = (this.page - 1) * this.perPage
      const end = start + this.perPage
      const slice = this.visibleUsers.slice(start, end).map(u => ({ ...u }))

      // 🔥 Índices do primeiro BLOCKED
      const globalBlockedIndex = this.visibleUsers.findIndex(u => u._label === 'BLOCKED')
      const pageBlockedIndex = slice.findIndex(u => u._label === 'BLOCKED')

      if (pageBlockedIndex !== -1) {
        const isGlobalTransition = globalBlockedIndex === start + pageBlockedIndex
        const isPageOnlyBlocked = pageBlockedIndex === 0

        if (isGlobalTransition || isPageOnlyBlocked) slice[pageBlockedIndex]._showWarningBefore = true
      }

      return slice
    },

    //
    // O USUÁRIO ESTÁ PESQUISANDO
    //

    isModeSearch() {
      // return this.search.trim().length >= SEARCH_MIN_LENGTH
      return this.debouncedSearch.trim().length >= SEARCH_MIN_LENGTH
    },

    //
    // O PLANO ATUAL DEVE LIMITAR A LISTA DE USUÁRIOS
    //

    USER_LIST_IS_LIMITED() {
      return (
        !this.freeVersion &&
        this.rawUsers.length > Number(this.userSession.userCapabilities.limiteUsuarios) &&
        isUserPlanOld(this.userSession.planId) === false
      )
    },

    //
    //
    //

    planUsers() {
      /* LISTA INVÁLIDA */ if (!Array.isArray(this.rawUsers)) return { allowed: [], blocked: [] }
      /* LISTA VAZIA */ if (!this.rawUsers.length) return { allowed: [], blocked: [] }
      /* LISTA SEM LIMITE */ if (!this.USER_LIST_IS_LIMITED) return { allowed: this.rawUsers, blocked: [] }

      const limit = Number(this.userSession.userCapabilities.limiteUsuarios)

      return {
        allowed: this.rawUsers.slice(0, limit),
        blocked: this.rawUsers.slice(limit).map(u => ({ ...u, _label: 'BLOCKED' })),
      }
    },

    //
    //
    //

    visibleUsers() {
      const { allowed, blocked } = this.planUsers

      const myID = this.myUser?.id
      const recentIDs = new Set(this.usuariosRecentes.map(u => u.id))
      const used = new Set()

      const push = (list, user, label = '') => {
        if (!user || used.has(user.id)) return
        used.add(user.id)
        list.push({ ...user, _label: label })
      }

      // 🔎 base de busca
      const searchFilter = user => !this.isModeSearch || userMatchesSearch(user, this.debouncedSearch /* this.search */)

      const result = []

      // 1️⃣ VOCÊ sempre no topo
      if (this.myUser && searchFilter(this.myUser)) {
        push(result, this.myUser, 'ME')
      }

      // 2️⃣ RECENTES (apenas allowed, fora da busca)
      if (!this.isModeSearch) {
        for (const id of recentIDs) {
          const user = allowed.find(u => u.id === id)
          push(result, user, 'RECENT')
        }
      }

      // 3️⃣ RESTANTE DOS PERMITIDOS
      for (const user of allowed) {
        if (user.id === myID) continue
        if (!searchFilter(user)) continue

        push(result, user, recentIDs.has(user.id) ? 'RECENT' : '')
      }

      // 4️⃣ BLOQUEADOS (apenas em busca ou listagem geral)
      for (const user of blocked) {
        if (!searchFilter(user)) continue
        push(result, user, 'BLOCKED')
      }

      return result
    },
  },

  watch: {
    visible: {
      handler (currentStatus, oldStatus)
      {
        if (oldStatus === true && currentStatus === false) {
          // Fechamento externo (overlay, ESC, v-model)
          this.onExternalClose()
        }
        // if (currentStatus === false) this.clearFields()
      },
    },

    page(val) {
      const maxPage = Math.max(1, Math.ceil(this.TOTAL_VISIBLE / this.perPage))
      if (val > maxPage) {
        this.page = maxPage
      }
    },

    search: {
      handler(val) {
        this._debounceSearch(val)
        if (this.page !== 1) this.page = 1
      }
    },
    /* search: {
      deep: true,
      handler (current, old) {
        if (this.page !== 1) this.page = 1
      },
    }, */
    rawUsers: {
      deep: true,
      handler (current, old) {
        if (this.page !== 1) this.page = 1
      },
    },
    perPage: {
      handler (current, old) {
        if (this.page !== 1) this.page = 1
      },
    },
  },

  created()
  {
    // console.log("::: [ADD USER MODAL] Created")

    this._debounceSearch = (function () {
      let t
      return v => {
        clearTimeout(t)
        t = setTimeout(() => {
          this.debouncedSearch = v
        }, 150)
      }
    }).call(this)
  },

  methods: {
    //
    //
    //

    prevPage() {
      if (this.page > 1) this.page--
    },

    nextPage() {
      if (this.page < this.TOTAL_PAGES) this.page++
    },

    //
    // Inclui na lista os usuários CRIADOS ou CONVIDADOS no "Não encotrou o usuário? Clique aqui"
    //

    onUserAddedToGroup({ user })
    {
      // console.log("::: onUserAddedToGroup:", user)

      // 1️⃣ evita duplicata
      if (this.rawUsers.some(u => u.id === user.id)) return

      // 2️⃣ adiciona na lista local
      this.rawUsers.push(user)

      // 3️⃣ opcional: marcar como recente
      // this.usuariosRecentes.unshift({ id: user.id })

      const viewerID = getUserUID()

      this.rawUsers.sort((a, b) => {
        const IS_ME_A = a.id === viewerID
        const IS_ME_B = b.id === viewerID
        // Compare by isMe first
        if (IS_ME_A && !IS_ME_B) return -1
        if (!IS_ME_A && IS_ME_B) return 1
        // If both have the same isMe value, compare by name
        return a.name.localeCompare(b.name, "pt-BR")
      })

      // 4️⃣ força reatividade (Vue 2 safe)
      // this.rawUsers = [...this.rawUsers]
      this.rawUsers = [...this.rawUsers].map(u => (u?._searchIndex ? u : { ...u, _searchIndex: buildSearchIndex(u) }))

      // Set recent users
      this.usuariosRecentes = this.getRecentUsers(this.groupID, this.rawUsers)

      //
      // Usuário convidado ou CRIADO E CONVIDADO pecisa ser propagado imediatamente no cache,
      // pois o polling pode levar alguns segundos para carregar, principalmente em uma escala
      // com MUITOS USUÁRIOS, e nesse caso, essa pequena espera para atualizar o CACHE pode fazer
      // com que o novo usuário não seja encontrado na próxima abertura do modal ATÉ QUE ele seja
      // fechado e o POLLING tenha tempo suficiente para atualizar o cache.
      //

      // Persist data in browser cache to speed up future loads
      EditScaleUsersCache.set(this.groupID, this.scaleID, this.rawUsers, this.lastUsersUpdateAt /* Mantém o TIMESTAMP antigo, mas será consertado pelo polling no próximo código 200 */)

      logDebug({
        box: `Usuários da escala "${this.scaleName || this.scaleID}"`,
        boxColor: 'yellow',
        title: 'Updated from AddUserToScaleModal',
        titleColor: '#1565C0',
        message: `${this.rawUsers.length} users received`,
      })
    },

    onSelectPage (pageID)
    {
      this.page = pageID
      // console.log('pageID:', pageID, this.page)
    },

    //
    //
    //
    // OPEN THIS WINDOW
    //
    //
    //

    resetMultiSelect() {
      this.selectedUsers = []
      this.isMultiSelectActive = false
      this._multiSelectIndex = 0
    },

    resetContext() {
      this._currentContextUser = null
      this._currentContextArgs = null
    },

    getRecentUsers (groupID, rawUsers)
    {
      // Lê recentes do cache
      const storageRecentUsers = useRecentUsersStorage({ groupID })
      const recentUsers = storageRecentUsers.getRecentUsers()

      // console.log("::: [ADD USER MODAL] recentUsers:", recentUsers)

      const allowedUserIDs = new Set(this.planUsers.allowed.map(u => u.id))

      // 1) Filtra apenas recentes permitidos e ordena por date (DESC)
      const orderedRecentIDs = recentUsers
      .filter(u => allowedUserIDs.has(u.userID))
      .sort((a, b) => b.date - a.date) /* 🔥 mais recente primeiro */
      .map(u => u.userID)

      // 2) Mapa rápido de usuários por id
      const usersById = new Map(rawUsers.map(u => [u.id, u]))

      // 3) Reconstrói recentUsers preservando a ordem cronológica
      return orderedRecentIDs.map(id => usersById.get(id)).filter(Boolean)
    },

    async open (args)
    {
      //
      // 🔹 Tenta hidratar do cache
      //

      const cached = EditScaleUsersCache.get(args.groupID, args.scaleID)

      // 🔹 só bloqueia se NÃO houver cache

      // if (!(cached?.users ?? [])?.length)
      if (!cached || !Object.prototype.hasOwnProperty.call((cached ?? {}), 'users'))
      {
        logDebug({
          box: `Usuários da escala "${args.scaleName || args.scaleID}"`,
          boxColor: 'white',
          title: 'AddUserToScale modal couldn\'t load from cache',
          titleColor: '#C62828',
          message: JSON.stringify(cached),
          // message: `${cached.users.length} users last updated at ${cached.lastModified}`,
        })

        return this.showMessage('Atenção!', 'Os plantonistas desta escala ainda não foram carregados.<br /><br />Aguarde alguns instantes e tente novamente ou atualize a tela para recarregar!')
      }

      logDebug({
        box: `Usuários da escala "${args.scaleName || args.scaleID}"`,
        boxColor: 'lightgreen',
        title: 'AddUserToScale modal user list loaded from cache',
        titleColor: '#2E7D32',
        message: `${cached.users.length} users last updated at ${cached.lastModified}`,
      })

      //
      //
      //

      const viewerID = getUserUID()

      // console.log(`::: "ADD USER TO SCALE" MODAL OPENED BY "${viewerID}":`, args)

      // Set scale details
      this.scaleID = args.scaleID
      this.scaleName = args.scaleName
      this.scaleLocation = args.scaleLocation
      this.scaleTurns = args.scaleTurns
      this.scaleUsers = args.scaleUsers
      /*
      // [staging-portaltelemedicina / master-portaltelemedicina]
      this.scaleConsultationDuration = args.scaleConsultationDuration
      this.slotType = args.slotType
      // [staging-portaltelemedicina / master-portaltelemedicina]
      */
      // Set group
      this.groupID = args.groupID
      this.grupoNome = args.grupoNome
      // Set shift
      this.turnID = args.turnID
      this.turnoDados = args.turnoDados
      // Set dates
      this.shiftDutyTaskStartDateDDMMYYYY = args.date.day.format('DD/MM/YYYY')
      this.shiftDutyTaskStartDateYYYYMD = args.date.day.format('YYYY-M-D')
      // Set pricing values from scale
      this.scaleValue = args.scaleValue || 0
      this.scaleWeekdaysHourlyRate = args.scaleWeekdaysHourlyRate || 0
      this.scaleWeekendHourlyRate = args.scaleWeekendHourlyRate || 0
      this.scaleDayShiftHourlyRate = args.scaleDayShiftHourlyRate || 0
      this.scaleNightShiftHourlyRate = args.scaleNightShiftHourlyRate || 0
      this.scaleWeekdaysDayShiftHourlyRate = args.scaleWeekdaysDayShiftHourlyRate || 0
      this.scaleWeekdaysNightShiftHourlyRate = args.scaleWeekdaysNightShiftHourlyRate || 0
      this.scaleWeekendDayShiftHourlyRate = args.scaleWeekendDayShiftHourlyRate || 0
      this.scaleWeekendNightShiftHourlyRate = args.scaleWeekendNightShiftHourlyRate || 0
      // Reset user selection
      this.resetMultiSelect()
      this.resetContext()
      // Set user/users
      const rawUsers = cached.users.map(u => ({ ...u, _searchIndex: buildSearchIndex(u) }))
      this.myUser = rawUsers.find(u => u.id === viewerID) || null
      this.lastUsersUpdateAt = cached.lastModified
      this.rawUsers = rawUsers
      // Set recent users
      this.usuariosRecentes = this.getRecentUsers(args.groupID, rawUsers)
      // Reset search terms
      this.search = ''

      // Reset navigation
      this.page = 1
      this.perPage = 50

      // Stop any possibility of present/show/display any loading in UI
      this.loading = false
      // Show the window
      this.visible = true
    },

    closeModal(reason = 'close', payload = null)
    {
      if (!this.visible) return

      this._closingIntentionally = true
      this.visible = false

      // emite evento para o pai
      this.$emit('closed', { reason, payload })
    },

    onExternalClose() {
     if (this._closingIntentionally) {
        // fechamento já tratado
        this._closingIntentionally = false
        return
      }

      this.clearFields()

      this.$emit('closed', { reason: 'overlay', payload: null })
    },

    //
    //
    //
    // FREE MEMORY AFTER CLOSING THE WINDOW
    //
    //
    //

    clearFields ()
    {
      // Reset scale details
      this.scaleID = ''
      this.scaleName = ''
      this.scaleLocation = { id: '', name: '' }
      this.scaleTurns = []
      this.scaleUsers = null
      /*
      // [staging-portaltelemedicina / master-portaltelemedicina]
      this.scaleConsultationDuration = 0
      this.slotType = "FREE"
      // [staging-portaltelemedicina / master-portaltelemedicina]
      */
      // Reset group
      this.groupID = ''
      this.grupoNome = ''
      // Reset shift
      this.turnID = ''
      this.turnoDados = null
      // Reset dates
      this.shiftDutyTaskStartDateDDMMYYYY = null
      this.shiftDutyTaskStartDateYYYYMD = null
      // Reset user selection
      this.resetMultiSelect()
      this.resetContext()
      // Reset user/users
      this.myUser = null
      this.lastUsersUpdateAt = null
      this.rawUsers = []
      // Reset recent users
      this.usuariosRecentes = []
      // Reset search terms
      this.search = ''
      // Reset navigation
      this.page = 1
      this.perPage = 50
    },

    //
    //
    //
    // OPEN SEARCH USER WINDOW (ADD OR INVITE)
    //
    //
    //

    openSearchUserWindow ()
    {
      this.$refs.searchUserDialog.open({ groupID: this.groupID })
    },

    //
    //
    //
    // ADD USER TO SCALE
    // - VALIDATION BEFORE ADDING
    // - OR ADDIND
    //
    //
    //
    // Retorna um objeto para indicar o que aconteceu:
    // { success: true } se adicionado com sucesso
    // { success: false, reason: 'blocked' } se bloqueado por regra ou erro
    // { success: false, reason: 'confirm_needed', type: CD_TYPE, data: CD_DATA } se precisa de confirmação
    async METHOD_ADD_USER_TO_SCALE (user, args = DEFAULT_ADD_ARGS())
    {
      if ( !Object.prototype.hasOwnProperty.call((user ?? {}), 'id') ) return { success: false, reason: 'invalid_user_data' }

      //
      // -----------------------------------------------------------------------------------------------
      // INITIALIZE SOME DATA
      // -----------------------------------------------------------------------------------------------
      //

      const { shiftDutyTaskStartDateYYYYMD, turnID, turnoDados, grupoNome, scaleName, scaleLocation } = this
      const { groupID, scaleID } = this // .$route.params

      if (!turnoDados || turnoDados.hour === undefined || turnoDados.hour === null || !shiftDutyTaskStartDateYYYYMD)
      {
          this.showMessage('Atenção!', 'Dados de plantão incompletos. Por favor, tente novamente.')

          return { success: false, reason: 'missing_turn_data' }
      }

      const plantaoPlantonistaID = user.id
      const plantaoPlantonistaNome = String(user.name).trim()
      const dutyStartTime = formatDbTime(turnoDados.hour)

      //
      // -----------------------------------------------------------------------------------------------
      // VERIFICA VIOLAÇÃO DE REGRAS
      // -----------------------------------------------------------------------------------------------
      //

      if (!!args.checkRuleViolations === true)
      {
        const ruleViolationResponse = await this.$store.dispatch(
          CHECK_RULE_VIOLATION,
          {
            userId: user.id,
            date: shiftDutyTaskStartDateYYYYMD,
            groupId: groupID,
            scaleId: scaleID,
            turnId: turnID,
            duration: turnoDados.duration,
            start_time: dutyStartTime,
          }
        )

        const { code, title, message, data } = ruleViolationResponse

        if (code !== 'OK')
        {
          this.showMessage(title, message)
          return { success: false, reason: 'rule_violation_error', message: message }
        }
        else if (code === 'OK' && data.violated)
        {
          const primeiroNome = user.name.split(" ")[0]
          const plantaoPlantonistaNomeStyled = String(primeiroNome).trim()

          if (data.action === "block")
          {
            this.showMessage("Ação bloqueada", `<div style="text-align: center"><b style="color: slateblue">${plantaoPlantonistaNomeStyled}</b> não pode ser adicionado devido a uma violação de regra:<br /><br />${data.message}</div>`)
            return { success: false, reason: 'rule_violation_blocked' }
          }
          else if (data.action === "warn")
          {
            // Retorna para o chamador que uma confirmação é necessária
            return {
              success: false,
              reason: 'confirm_needed',
              type: CHECK_RULE_VIOLATION,
              data: { user: user, args: args },
              title: "Violação de Regra!",
              text: `<div style="text-align: center"><b style="color: slateblue">${plantaoPlantonistaNomeStyled}</b> possui uma violação de regra para <b style="color: orangered">${this.shiftDutyTaskStartDateDDMMYYYY} às ${dutyStartTime}</b>:<br /><br />${data.message}<br /><br /><b>Deseja adicionar o plantonista mesmo assim?</b></div>`,
              buttonPosition: "right",
              width: 330,
              closeText: "Não",
              continueText: "Sim",
            }
          }
        }
      }

      //
      // -----------------------------------------------------------------------------------------------
      // O PLANTONISTA JÁ FOI ADICIONADO EM OUTRAS ESCALAS NA DATA E TURNO DESEJADOS?
      // Nota: Apenas nas escalas (editar escala) do mesmo grupo
      // -----------------------------------------------------------------------------------------------
      //

      if (!!args.checkScaleDutyManualConflicts === true)
      {
        const foundConflicts = await apiCheckUserDutyConflictInGroup(groupID, user.id, shiftDutyTaskStartDateYYYYMD, turnoDados.hour)

        if (foundConflicts.length > 0)
        {
          let htmlTable = '<table class="tableConflicts">'
          htmlTable += '<tr><th style="padding: 0 7px">Escala</th><th style="padding: 0 7px">Quantidade</th></tr>'
          foundConflicts.forEach(r => {
            htmlTable += `<tr><td style="padding: 4px 7px; line-height: 0; word-break: break-all"><b style="color: indianred; line-height: 19px">${r.scaleName}</b></td><td><span style="display: inline-block; width: 100%; color: slategrey; font-size: 14px; line-height: 14px">${r.amount}</span></td></tr>`
          })
          htmlTable += '</table>'

          // Retorna para o chamador que uma confirmação é necessária
          return {
            success: false,
            reason: 'confirm_needed',
            type: 'CHECK_EXISTING_DUTIES_CONFLICT_BEFORE_ADDING',
            data: { user: user, args: args }, // Adiciona user e args para o cdAction
            title: 'Conflito!',
            text: `<div style="text-align: center"><b style="color: slateblue">${plantaoPlantonistaNome || 'O plantonista'}</b> já foi adicionado em <b style="color: orangered">${this.shiftDutyTaskStartDateDDMMYYYY} às ${dutyStartTime}</b> no grupo <b style="color: forestgreen">${grupoNome}</b>:<br /><br />${htmlTable}<br /><b>Deseja adicionar o plantonista mesmo assim?</b></div>`,
            buttonPosition: 'right',
            width: 330,
            closeText: 'Não',
            continueText: 'Sim',
          }
        }
      }

      //
      // -----------------------------------------------------------------------------------------------
      // PROCESSAR JORNADA PARA VERIFICAR CONFLITOS DE JORNADA NA ESCALA ATUAL
      // PROCESSAR JORNADA PARA GERAR PLANTÕES CONFORME A JORNADA SELECIONADA
      // -----------------------------------------------------------------------------------------------
      //

      const { jornada, journeyWorkingDays, journeyDaysOff } = this.$store.state.app.EditScale

      const processedWeeks = this.$store.state.app.EditScale.weeks.map(week => ({
        ...week,

        //
        // VERIFICA SE O PLANTÃO PODE SER COLOCADO NO TURNO
        // > SE SIM, ADICIONA NA ESCALA/PLANTÕES
        // > CASO CONTRÁRIO, MOSTRA UM ERRO/AVISO NA TELA
        //

        isReleasedWeek: (
          this.scaleTurns.length > 0 &&
          week.value.some(day => this.scaleTurns.some(turn => turn.plantoes && turn.plantoes[day.dayFmtWzero] && turn.plantoes[day.dayFmtWzero].confirmedDutys))
        ),
      }))

      //
      // -----------------------------------------------------------------------------------------------
      // CRIAR PLANTÕES (VER E EDITAR) CONFORME A JORNADA DESEJADA
      // -----------------------------------------------------------------------------------------------
      //

      const userScaleHourlyRate = (this.scaleUsers ?? {})?.[plantaoPlantonistaID]?.hourlyrate || 0
      const userDataToPass = (this.scaleUsers ?? {})?.[plantaoPlantonistaID] || null

      // console.log("::: [ADD USER MODAL] userScaleHourlyRate:", userScaleHourlyRate)

      const fbUpdate = await createShift({
        /*
        // [staging-portaltelemedicina / master-portaltelemedicina]
        slotType: this.slotType,
        scaleConsultationDuration: this.scaleConsultationDuration,
        // [staging-portaltelemedicina / master-portaltelemedicina]
        */

        user: {
          uid: plantaoPlantonistaID,
          img: user.image || '',
          nome: user.name,
        },
        userScaleHourlyRate,
        userData: userDataToPass,
        scaleData: {
          weekdaysHourlyRate: this.scaleWeekdaysHourlyRate,
          weekendHourlyRate: this.scaleWeekendHourlyRate,
          dayShiftHourlyRate: this.scaleDayShiftHourlyRate,
          nightShiftHourlyRate: this.scaleNightShiftHourlyRate,
          weekdaysDayShiftHourlyRate: this.scaleWeekdaysDayShiftHourlyRate,
          weekdaysNightShiftHourlyRate: this.scaleWeekdaysNightShiftHourlyRate,
          weekendDayShiftHourlyRate: this.scaleWeekendDayShiftHourlyRate,
          weekendNightShiftHourlyRate: this.scaleWeekendNightShiftHourlyRate,
          hourlyrate: this.scaleValue,
        },
        jornada,
        journeyWorkingDays,
        journeyDaysOff,
        weeks: processedWeeks,
        turnID,
        turnoDados,
        dataSemZero: shiftDutyTaskStartDateYYYYMD,
        groupID,
        grupoNome,
        scaleID,
        scaleName,
        scaleLocation,
      })

      // console.log('::: METHOD_ADD_USER_TO_SCALE => fbUpdate:', fbUpdate)

      if (fbUpdate.code === 0)
      {
        this.showMessage('Atenção!', 'Não foi possível criar o plantão. Por favor, tente novamente ou fale com o nosso suporte.')
        return { success: false, reason: 'shift_creation_failed' }
      }

      //
      // -----------------------------------------------------------------------------------------------
      // O PLANTONISTA JÁ FOI ADICIONADO NAS DATAS DA JORNADA DESEJADA?
      // Nota: Apenas no editar escala da escala atual
      // -----------------------------------------------------------------------------------------------
      //

      const datasDaJornada = fbUpdate.dates

      if (!!args.checkScaleDutyJourneyConflicts === true)
      {
        const conflictDays = []

        for (var i = 0; i < datasDaJornada.length; i++)
        {
          // Parece que o firebase depende de zeros a esquerda... sem isso a consulta não funciona (Idéia: mixin para padronizar 0's em todo app)

          const journeyDateYYYYMD = datasDaJornada[i]
          // console.log("::: journeyDateYYYYMD:", journeyDateYYYYMD)

          if (await apiIsUserInDuty(groupID, scaleID, turnID, journeyDateYYYYMD, user.id))
          {
            // Objeto Date a partir de uma script YYYY-M-D
            const journeyDateObjectFromYYYYMDstring = parseYYYYMD(journeyDateYYYYMD)

            // Gera string DD/MM/YYYY a partir do objeto Date
            conflictDays.push(`<span style="color: steelblue">${formatDDMMYYYY(journeyDateObjectFromYYYYMDstring)}</span>`)
          }
        }

        // Senhor Jesus
        // COMO TRATAR: Se um plantão criado foi passado para outro usuário, o conflito continuará funcionando aqui.

        if (conflictDays.length > 0)
        {
          // Retorna para o chamador que uma confirmação é necessária
          return {
            success: false,
            reason: 'confirm_needed',
            type: 'CHECK_EXISTING_DUTIES_CONFLICT_BEFORE_ADDING',
            data: { user: user, args: args },
            title: 'Conflito!',
            text: `<div style="text-align: center"><b style="color: slateblue">${plantaoPlantonistaNome || 'O usuário'}</b> já foi adicionado na escala atual.<br /><br />Veja os conflitos abaixo:<br />${conflictDays.join('<br />')}<br /><br /><b>Deseja adicionar o plantonista mesmo assim?</b></div>`,
            buttonPosition: 'right',
            width: 330,
            closeText: 'Não',
            continueText: 'Sim',
          }
        }
      }

      //
      // -----------------------------------------------------------------------------------------------
      // O PLANTONISTA JÁ TEM PLANTÕES DISPARADOS NAS DATAS DA JORNADA DESEJADA?
      // Nota: Verificará plantões disparados em todos os grupos que o plantonista faz parte
      // -----------------------------------------------------------------------------------------------
      //

      if (!!args.checkDutyConflicts === true)
      {
        const firstWeek = processedWeeks[0]
        const firstDate = firstWeek.value[0].day

        const lastWeek = processedWeeks[processedWeeks.length - 1]
        const lastDate = lastWeek.value[6].day

        //
        // Mostra conflitos caso algum usuário tenha outro plantão no mesmo horário
        //

        const journeyDates = datasDaJornada.map(JOURNEY_DATE_YYYYMD_STRING =>
          formatDDMMYYYY(parseYYYYMD(JOURNEY_DATE_YYYYMD_STRING))
        )

        const { conflictedScales = [], conflictsTableHtml = '' } = await canAddUserShift(dutyStartTime, journeyDates, plantaoPlantonistaID, firstDate, lastDate)

        if (conflictedScales.length > 0)
        {
          // Retorna para o chamador que uma confirmação é necessária
          return {
            success: false,
            reason: 'confirm_needed',
            type: 'ADD_USER',
            data: { user: user, args: args },
            title: 'Conflito!',
            text: `<div style="text-align: center">O plantão ${plantaoPlantonistaNome.length > 0 ? `de <b style="color: slateblue">${plantaoPlantonistaNome}</b>` : 'do <b style="color: slateblue">usuário selecionado</b>'} já foi criado ${ journeyDates.length === 1 ? `em <b style="color: orangered">${this.shiftDutyTaskStartDateDDMMYYYY} às ${dutyStartTime}</b>` : ` no turno das <b style="color: orangered">${dutyStartTime}</b> em uma ou mais datas da jornada <b style="color: steelblue">${jornada}</b>` }:<br /><br />${conflictsTableHtml}<br /><b>Deseja adicionar o plantonista mesmo assim?</b></div>`,
            buttonPosition: 'right',
            width: 330,
            closeText: 'Não',
            continueText: 'Sim',
          }
        }
      }

      //
      // -----------------------------------------------------------------------------------------------
      // EXECUTA ESCRITA NO FIREBASE
      // -----------------------------------------------------------------------------------------------
      //

      const response = await fbFullUpdate(fbUpdate.firebasePaths)

      if (response.code !== 1) {
        return { success: false, reason: 'firebase_update_failed' }
      }

      // JESUS: Testar
      const isMyUser = this.myUser?.id === plantaoPlantonistaID

      if (!isMyUser) {
        this.addRecentUsersOnStorage({ plantaoPlantonistaID, fbUpdate })
      }

      this.$store.commit('SET_EDIT_SCALE_CTRL_Z', { type: 'add', data: fbUpdate.firebasePaths })

      return { success: true }
    },

    addRecentUsersOnStorage({ plantaoPlantonistaID, fbUpdate }) {
      const storageRecentUsers = useRecentUsersStorage({ groupID: this.groupID })
      storageRecentUsers.addRecentUsers({ userID: plantaoPlantonistaID })

      //
      // Nota em 18/Mar/2026:
      // Movido em outro bloco DESSE ARQUIVO para não ser limitado POR TIPO DE USUÁRIO
      //

      /* this.$store.commit('SET_EDIT_SCALE_CTRL_Z', { type: 'add', data: fbUpdate.firebasePaths }) */
    },

    //
    //
    //
    // CONFIRM DIALOG OUTPUT/RESPONSE
    //
    //
    //

    async cdAction (cdResponse)
    {
      //
      // Fechou algum modal
      //

      if (cdResponse === false)
      {
        // this.loading = false
        if (this.isMultiSelectActive) {
          if (this.selectedUsers.length < 2) {
            // console.log('CANCELOU MODAL COM MULTI SELEÇÃO ATIVA e não atendeu o critério mínimo para iniciar a execução...')
            // O USUÁRIO FECHOU A CAIXA DE MENSAGEM QUE INSTRUI A SELECIONAR NO MÍNIMO DOIS USUÁRIOS PARA PROSSEGUIR COM A OPERAÇÃO
          } else {
            // console.log('CANCELOU MODAL COM MULTI SELEÇÃO ATIVA, mas deve continuar processando usuários selecionados...')
            await this.processNextSelectedUser("NEXT")
          }
        } else {
          // console.log('CANCELOU MODAL SEM MULTI SELEÇÃO')
          // limpa contexto para seleção única
          this.resetContext()
        }
        return
      }

      //
      // /
      //

      const user = this._currentContextUser
      const args = this._currentContextArgs

      if (!user || !args) {
        // this.loading = false
        this.resetMultiSelect()
        this.resetContext()
        return this.showMessage('Atenção!', 'Não foi possível continuar adicionando usuários, pois o contexto da ação foi perdido.')
      }

      const updatedArgs = { ...args }

      if (cdResponse.cdType === CHECK_RULE_VIOLATION) {
        updatedArgs.checkRuleViolations = false
      } else if (cdResponse.cdType === 'CHECK_EXISTING_DUTIES_CONFLICT_BEFORE_ADDING') {
        updatedArgs.checkScaleDutyManualConflicts = false
        updatedArgs.checkScaleDutyJourneyConflicts = false
      } else if (cdResponse.cdType === 'ADD_USER') {
        updatedArgs.checkDutyConflicts = false
      }

      const result = await this.runAddUserFlow(user, updatedArgs)

      if (result.status === 'success') {
        if (this.isMultiSelectActive) {
          await this.processNextSelectedUser("NEXT")
        } else {
          this.closeModal('success', { user })
        }
      } else if (result.status === 'confirm') {
        await this.showConfirmBox(result.payload)
      }
    },

    async runAddUserFlow(user, args = DEFAULT_ADD_ARGS()) {
      this._currentContextUser = user
      this._currentContextArgs = args

      this.loading = true
      const result = await this.METHOD_ADD_USER_TO_SCALE(user, args)
      this.loading = false

      if (result.success) {
        return { status: 'success' }
      }

      if (result.reason === 'confirm_needed') {
        return { status: 'confirm', payload: result }
      }

      return { status: 'error', payload: result }
    },

    //
    // Usuário ATIVOU ou DESATIVOU a multi seleção de usuários
    //

    toggleMultiSelect()
    {
      this.isMultiSelectActive = !this.isMultiSelectActive
      if (!this.isMultiSelectActive) {
        this.resetMultiSelect()
        this.resetContext()
      }
    },

    //
    // Callback do ITEM DE LISTA responsável pelo evento de CLICK, ou seja:
    // - Quando o usuário marcou ou desmarcou um ITEM no modo MULTI SELEÇÃO
    // - ou apenas clicou no usuário que será inserido no modo SELEÇÃO ÚNICA
    //

    async toggleUserSelection(user) {
      // 👉 seleção única
      if (!this.isMultiSelectActive) {
        this.runUniqueUserSelection(user)
        return
      }

      // 👉 seleção múltipla (somente toggle visual)
      const index = this.selectedUsers.findIndex(u => u.id === user.id)

      if (index > -1) {
        this.selectedUsers.splice(index, 1)
      } else {
        this.selectedUsers.push(user)
      }
    },

    // Usado no modo SELEÇÃO ÚNICA para inserir um único usuário

    async runUniqueUserSelection(user) {
      if (this.loading) return // Previne mais de uma execução em paralelo quando não está em MULTI SELEÇÃO e tentou clicar várias vezes (ou outro glitch que causaria multiplas execuções)

      const result = await this.runAddUserFlow(user)

      if (result.status === 'success') {
        this.closeModal('success', { user })
      } else if (result.status === 'confirm') {
        await this.showConfirmBox(result.payload)
      } else if (result.status === 'error') {
        this.showMessage('Atenção!', `Não foi possível adicionar ${user.name}`)
      }
    },

    //
    // Cada componente de ITEM DA LISTA usa essa função para saber se ele deve mostrar a caixa como MARCADA ou NÃO
    //

    isUserSelected(id)
    {
      return this.selectedUsers.some(u => u.id === id)
    },

    //
    // Usuário INICIOU a inserção de cada usuário selecionado
    //

    async executeMultipleSelection() {
      if (this.selectedUsers.length < 2) return this.showMessage('Atenção!', 'Selecione no mínimo dois usuários.')

      await this.processNextSelectedUser("START")
    },

    //
    // Processa a inserção do próximo usuário selecionado
    //

    async processNextSelectedUser(command) {
      if (this.loading) return

      if (command === "START") {
        this._multiSelectIndex = 0
      } else if (command === "NEXT") {
        this._multiSelectIndex++
      }

      if (this._multiSelectIndex >= this.selectedUsers.length) {
        // Terminou a execução da lista
        this.resetMultiSelect()
        this.resetContext()
        this.closeModal('multi_success', { users: this.selectedUsers })
        return
      }

      const user = this.selectedUsers[this._multiSelectIndex]
      const result = await this.runAddUserFlow(user)

      if (result.status === 'success') {
        await this.processNextSelectedUser("NEXT")
      } else if (result.status === 'confirm') {
        await this.showConfirmBox(result.payload)
      } else {
        this.showMessage('Atenção!', `Não foi possível adicionar ${user.name}, seguindo para o próximo...`)
        await this.processNextSelectedUser("NEXT")
      }
    },

    handleSearchInputChange(event)
    {
      const text = this.search.trim()

      // console.log("::: text:", event?.target?.value || "", `${text}`)

      this.SHOW_HINT = text.length > 0 && text.length < SEARCH_MIN_LENGTH
    }
  }
}
